import { useEffect, useMemo, useState } from "react";
import { galleryPlaceholders, galleryCategories } from "@/data/content";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X, Expand } from "lucide-react";
import { usePhotos } from "@/hooks/usePhotos";

export default function Gallery() {
  const { photos: rawPhotos, loaded } = usePhotos();
  const [activeCat, setActiveCat] = useState("all");
  const [api1, setApi1] = useState(null);
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState({ open: false, idx: 0 });

  const uploaded = useMemo(
    () =>
      rawPhotos
        .filter((p) => p.category !== "hero" && p.category !== "hosts")
        .map((p) => ({
          url: p.data_url,
          caption: p.caption || "",
          category: p.category || "all",
        })),
    [rawPhotos]
  );

  // Use uploaded photos when present, else fall back to placeholders ONLY
  // after the fetch has resolved (avoids flashing placeholders on first load).
  const allPhotos = uploaded.length > 0
    ? uploaded
    : loaded
      ? galleryPlaceholders
      : [];

  const photos = useMemo(() => {
    if (activeCat === "all") return allPhotos;
    return allPhotos.filter((p) => p.category === activeCat);
  }, [allPhotos, activeCat]);

  // Reset carousel position on category change
  useEffect(() => {
    if (api1) {
      api1.scrollTo(0, true);
      setCurrent(0);
    }
  }, [activeCat, api1, photos.length]);

  useEffect(() => {
    if (!api1) return;
    const onSelect = () => setCurrent(api1.selectedScrollSnap());
    api1.on("select", onSelect);
    onSelect();
    return () => {
      api1?.off?.("select", onSelect);
    };
  }, [api1]);

  const goTo = (i) => api1?.scrollTo(i);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-20 md:py-28 bg-[#FAF7F2]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="overline">The home</p>
            <h2
              data-testid="gallery-title"
              className="font-serif-display text-4xl md:text-5xl mt-3 text-[#232E26]"
            >
              A boutique retreat,<br className="hidden md:block" />
              <em className="not-italic text-[#C65D47]">framed by olive trees.</em>
            </h2>
          </div>
          <p className="text-[#5B6E4A] max-w-md leading-relaxed">
            Step through each room, the bathroom, the pool and the
            sun-drenched exterior of Mirabella Anemoessa.
          </p>
        </div>

        {/* Category tabs */}
        <div
          data-testid="gallery-categories"
          className="flex flex-wrap items-center gap-2 md:gap-3 mb-8"
        >
          {galleryCategories.map((c) => {
            const count =
              c.id === "all"
                ? allPhotos.length
                : allPhotos.filter((p) => p.category === c.id).length;
            const active = activeCat === c.id;
            return (
              <button
                key={c.id}
                data-testid={`gallery-cat-${c.id}`}
                onClick={() => setActiveCat(c.id)}
                disabled={count === 0}
                className={`px-4 py-2 text-xs tracking-[0.2em] uppercase border transition-colors rounded-sm
                  ${
                    active
                      ? "bg-[#232E26] text-[#FAF7F2] border-[#232E26]"
                      : "bg-transparent text-[#232E26] border-[#5B6E4A]/30 hover:border-[#232E26]"
                  }
                  ${count === 0 ? "opacity-30 cursor-not-allowed" : ""}`}
              >
                {c.label}
                <span className="ml-2 opacity-60 normal-case tracking-normal text-[10px]">
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {photos.length === 0 ? (
          <div className="bg-[#EAE0D5]/60 p-16 text-center text-[#5B6E4A]">
            {loaded ? "No photos in this category yet." : "Loading photos…"}
          </div>
        ) : (
          <div className="relative">
            <Carousel
              setApi={setApi1}
              opts={{ loop: photos.length > 1, align: "center" }}
              data-testid="gallery-carousel"
              className="bg-[#EAE0D5]"
            >
              <CarouselContent>
                {photos.map((p, i) => (
                  <CarouselItem
                    key={i}
                    data-testid={`gallery-slide-${i}`}
                    className="basis-full"
                  >
                    <button
                      onClick={() => setLightbox({ open: true, idx: i })}
                      className="relative w-full aspect-[16/9] md:aspect-[21/10] overflow-hidden group block"
                      data-testid={`gallery-slide-open-${i}`}
                    >
                      <img
                        src={p.url}
                        alt={p.caption || "Mirabella Anemoessa"}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                        loading={i === 0 ? "eager" : "lazy"}
                      />
                      <div className="absolute top-4 right-4 bg-[#FAF7F2]/90 backdrop-blur px-3 py-2 text-xs uppercase tracking-[0.2em] text-[#232E26] flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Expand size={12} /> Fullscreen
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8 bg-gradient-to-t from-black/60 via-black/15 to-transparent">
                        <p className="text-white font-serif-display text-xl md:text-2xl">
                          {p.caption}
                        </p>
                      </div>
                    </button>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {/* Custom controls */}
            <div className="flex items-center justify-between mt-5">
              <div className="flex items-center gap-2 text-sm text-[#5B6E4A]">
                <span
                  data-testid="gallery-counter"
                  className="font-serif-display text-[#232E26] text-xl"
                >
                  {String(current + 1).padStart(2, "0")}
                </span>
                <span className="opacity-60">/ {String(photos.length).padStart(2, "0")}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="gallery-prev"
                  onClick={() => api1?.scrollPrev()}
                  className="h-11 w-11 border border-[#5B6E4A]/30 hover:bg-[#232E26] hover:text-[#FAF7F2] hover:border-[#232E26] transition-colors flex items-center justify-center"
                  aria-label="Previous slide"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  data-testid="gallery-next"
                  onClick={() => api1?.scrollNext()}
                  className="h-11 w-11 border border-[#5B6E4A]/30 hover:bg-[#232E26] hover:text-[#FAF7F2] hover:border-[#232E26] transition-colors flex items-center justify-center"
                  aria-label="Next slide"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>

            {/* Thumbnails */}
            {photos.length > 1 && (
              <div
                data-testid="gallery-thumbnails"
                className="mt-6 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2"
              >
                {photos.map((p, i) => (
                  <button
                    key={i}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => goTo(i)}
                    className={`relative aspect-[4/3] overflow-hidden border-2 transition-all ${
                      current === i
                        ? "border-[#C65D47] opacity-100"
                        : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={p.url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog
        open={lightbox.open}
        onOpenChange={(o) => setLightbox((s) => ({ ...s, open: o }))}
      >
        <DialogContent
          data-testid="gallery-lightbox"
          className="max-w-6xl p-0 bg-[#232E26] border-0"
        >
          <DialogTitle className="sr-only">Gallery image</DialogTitle>
          <button
            data-testid="lightbox-close"
            onClick={() => setLightbox((s) => ({ ...s, open: false }))}
            className="absolute top-3 right-3 z-20 p-2 text-white/80 hover:text-white"
            aria-label="Close"
          >
            <X size={22} />
          </button>
          {photos[lightbox.idx] && (
            <img
              src={photos[lightbox.idx].url}
              alt={photos[lightbox.idx].caption || "Gallery"}
              className="w-full max-h-[85vh] object-contain"
            />
          )}
          {photos[lightbox.idx]?.caption && (
            <div className="px-6 pb-5 pt-2 text-center text-white/85 text-sm">
              {photos[lightbox.idx].caption}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
